import type {
  LogicRule,
  LogicCondition,
  LogicEffect,
  NutritionModifier,
  RuleRow,
  RuleEvaluationResult,
  UserSelections
} from '@/types/logicRules'

// Re-export types for consumers
export type { RuleEvaluationResult, UserSelections } from '@/types/logicRules'

/**
 * Rule Evaluation Engine
 *
 * Evaluates logic rules based on user selections and returns effects to apply.
 *
 * Flow:
 * 1. Sort rules by priority (higher priority first)
 * 2. For each active rule:
 *    - Evaluate all conditions
 *    - If conditions pass (based on operator: all/any), apply all effects
 * 3. Return aggregated results
 */

/**
 * Evaluate a single condition based on user selections
 */
function evaluateCondition(
  condition: LogicCondition,
  selections: UserSelections
): boolean {
  const rowSelection = selections[condition.trigger_row_id]

  switch (condition.condition_type) {
    case 'ingredient_selected': {
      if (!rowSelection) return false

      const ingredients = Array.isArray(rowSelection) ? rowSelection : [rowSelection]

      // If trigger_row_ingredient_id is null, any ingredient selection passes
      if (!condition.trigger_row_ingredient_id) {
        return ingredients.length > 0
      }

      // Check if specific ingredient is selected
      return ingredients.some(ing => ing.id === condition.trigger_row_ingredient_id)
    }

    case 'ingredient_not_selected': {
      if (!rowSelection) return true

      const ingredients = Array.isArray(rowSelection) ? rowSelection : [rowSelection]

      // If trigger_row_ingredient_id is null, passes if no ingredients selected
      if (!condition.trigger_row_ingredient_id) {
        return ingredients.length === 0
      }

      // Check if specific ingredient is NOT selected
      return !ingredients.some(ing => ing.id === condition.trigger_row_ingredient_id)
    }

    case 'selection_count': {
      if (!rowSelection) return false

      const ingredients = Array.isArray(rowSelection) ? rowSelection : [rowSelection]
      const count = condition.metadata?.count ?? 0

      return ingredients.length === count
    }

    case 'row_selected': {
      // Row is selected if it has any ingredient selection
      return !!rowSelection && (
        Array.isArray(rowSelection) ? rowSelection.length > 0 : true
      )
    }

    case 'multiple_ingredients_selected': {
      if (!rowSelection) return false

      const ingredients = Array.isArray(rowSelection) ? rowSelection : [rowSelection]
      const requiredIds = condition.metadata?.ingredient_ids ?? []

      // Check if ALL required ingredients are selected
      return requiredIds.every(reqId =>
        ingredients.some(ing => ing.id === reqId)
      )
    }

    default:
      console.warn(`Unknown condition type: ${condition.condition_type}`)
      return false
  }
}

/**
 * Evaluate all conditions for a rule based on operator
 */
function evaluateRuleConditions(
  rule: LogicRule,
  selections: UserSelections
): boolean {
  if (rule.conditions.length === 0) return false

  if (rule.operator === 'all') {
    // ALL conditions must pass (AND logic)
    return rule.conditions.every(condition =>
      evaluateCondition(condition, selections)
    )
  } else {
    // ANY condition must pass (OR logic)
    return rule.conditions.some(condition =>
      evaluateCondition(condition, selections)
    )
  }
}

/**
 * Apply effects to the result object
 */
function applyEffects(
  effects: LogicEffect[],
  result: RuleEvaluationResult,
  rows?: RuleRow[],
  triggeringIngredientId?: string | null
): void {
  for (const effect of effects) {
    switch (effect.effect_type) {
      case 'show_row':
        // Only show if not explicitly hidden (hide_row takes precedence)
        if (!result.hidden_rows.has(effect.target_row_id)) {
          result.visible_rows.add(effect.target_row_id)
        }
        break

      case 'hide_row': {
        // Check if row can be hidden (respect can_be_hidden = false)
        const targetRow = rows?.find(r => r.id === effect.target_row_id)
        if (targetRow && targetRow.can_be_hidden === false) {
          break
        }
        // Row can be hidden - apply the effect
        result.hidden_rows.add(effect.target_row_id)
        result.visible_rows.delete(effect.target_row_id)
        break
      }

      case 'show_next_row':
        // show_next_row needs context-level handling (row order + trigger source)
        break

      case 'set_row_variation':
        if (effect.target_row_variation_id) {
          result.active_variations[effect.target_row_id] = effect.target_row_variation_id
        }
        break

      case 'enable_ingredient':
        if (effect.target_row_ingredient_id) {
          if (!result.enabled_ingredients[effect.target_row_id]) {
            result.enabled_ingredients[effect.target_row_id] = new Set()
          }
          result.enabled_ingredients[effect.target_row_id].add(effect.target_row_ingredient_id)

          // Remove from disabled if present
          result.disabled_ingredients[effect.target_row_id]?.delete(effect.target_row_ingredient_id)
        }
        break

      case 'disable_ingredient':
        if (effect.target_row_ingredient_id) {
          if (!result.disabled_ingredients[effect.target_row_id]) {
            result.disabled_ingredients[effect.target_row_id] = new Set()
          }
          result.disabled_ingredients[effect.target_row_id].add(effect.target_row_ingredient_id)

          // Remove from enabled if present
          result.enabled_ingredients[effect.target_row_id]?.delete(effect.target_row_ingredient_id)
        }
        break

      case 'multiply_nutrition':
      case 'apply_nutrition_multiplier': {
        const multiplier = effect.metadata?.multiplier ?? 1
        const existingModifier = result.nutrition_modifiers[effect.target_row_id]

        // If there's already a divide modifier, create multiply_then_divide
        if (existingModifier && existingModifier.type === 'divide') {
          result.nutrition_modifiers[effect.target_row_id] = {
            type: 'multiply_then_divide',
            value: multiplier,
            multiplier,
            divide_by: existingModifier.divide_by || existingModifier.value,
            rounding: existingModifier.rounding,
            rowId: effect.target_row_id
          }
        } else {
          result.nutrition_modifiers[effect.target_row_id] = {
            type: 'multiply',
            value: multiplier,
            rowId: effect.target_row_id
          }
        }
        break
      }

      case 'divide_nutrition': {
        const divisor = effect.metadata?.divisor ?? 1
        result.nutrition_modifiers[effect.target_row_id] = {
          type: 'divide',
          value: divisor,
          rowId: effect.target_row_id
        }
        break
      }

      case 'divide_nutrition_by_count': {
        const divideBy = effect.metadata?.divide_by ?? 1
        const rounding = effect.metadata?.rounding ?? 'round'
        const existingModifier = result.nutrition_modifiers[effect.target_row_id]

        // If there's already a multiply modifier, create multiply_then_divide
        if (existingModifier && existingModifier.type === 'multiply') {
          result.nutrition_modifiers[effect.target_row_id] = {
            type: 'multiply_then_divide',
            value: existingModifier.value,
            multiplier: existingModifier.value,
            divide_by: divideBy,
            rounding,
            rowId: effect.target_row_id
          }
        } else {
          result.nutrition_modifiers[effect.target_row_id] = {
            type: 'divide',
            value: typeof divideBy === 'number' ? divideBy : 1,
            divide_by: divideBy,
            rounding,
            rowId: effect.target_row_id
          }
        }
        break
      }

      case 'set_selection_limit':
      case 'set_selection_limits': {
        const maxSelections = effect.metadata?.max_selections
        if (maxSelections !== undefined) {
          result.selection_limits[effect.target_row_id] = maxSelections
        }
        break
      }

      case 'clear_row_selections': {
        // Mark this row to have its selections cleared
        // If triggeringIngredientId is provided, keep that ingredient selected
        // Otherwise, clear all selections
        result.rows_to_clear.set(effect.target_row_id, triggeringIngredientId || null)
        break
      }

      default:
        console.warn(`Unknown effect type: ${effect.effect_type}`)
    }
  }
}

/**
 * Main rule evaluation function
 *
 * @param rules - Array of logic rules from the meal
 * @param selections - Current user selections (rowId -> ingredient(s))
 * @param rows - Optional array of rows to check can_be_hidden property
 * @returns Evaluation result with all effects to apply
 */
export function evaluateRules(
  rules: LogicRule[],
  selections: UserSelections,
  rows?: RuleRow[]
): RuleEvaluationResult {
  // Initialize result with empty sets and objects
  const result: RuleEvaluationResult = {
    visible_rows: new Set(),
    hidden_rows: new Set(),
    active_variations: {},
    enabled_ingredients: {},
    disabled_ingredients: {},
    nutrition_modifiers: {},
    selection_limits: {},
    rows_to_clear: new Map()
  }

  // Sort rules by priority (higher priority first)
  const sortedRules = [...rules].sort((a, b) => b.priority - a.priority)

  // Evaluate each rule
  for (const rule of sortedRules) {
    if (!rule.is_active) continue

    const conditionsPassed = evaluateRuleConditions(rule, selections)

    if (conditionsPassed) {
      // Find the ingredient that triggered this rule (for clear_row_selections)
      let triggeringIngredientId: string | null = null
      for (const condition of rule.conditions) {
        if (condition.condition_type === 'ingredient_selected' && condition.trigger_row_ingredient_id) {
          const rowSelection = selections[condition.trigger_row_id]
          if (rowSelection) {
            const ingredients = Array.isArray(rowSelection) ? rowSelection : [rowSelection]
            if (ingredients.some(ing => ing.id === condition.trigger_row_ingredient_id)) {
              triggeringIngredientId = condition.trigger_row_ingredient_id
              break
            }
          }
        }
      }

      applyEffects(rule.effects, result, rows, triggeringIngredientId)
    }
  }

  return result
}

/**
 * Helper function to check if a row should be visible
 *
 * @param rowId - Row ID to check
 * @param result - Evaluation result
 * @param defaultVisible - Default visibility if no rules apply
 * @returns True if row should be visible
 */
export function isRowVisible(
  rowId: string,
  result: RuleEvaluationResult,
  defaultVisible: boolean = true
): boolean {
  // If explicitly hidden, not visible
  if (result.hidden_rows.has(rowId)) return false

  // If explicitly visible, visible
  if (result.visible_rows.has(rowId)) return true

  // Otherwise use default
  return defaultVisible
}

/**
 * Helper function to check if an ingredient should be enabled
 *
 * @param rowId - Row ID
 * @param ingredientId - Ingredient ID to check
 * @param result - Evaluation result
 * @param defaultEnabled - Default enabled state if no rules apply
 * @returns True if ingredient should be enabled
 */
export function isIngredientEnabled(
  rowId: string,
  ingredientId: string,
  result: RuleEvaluationResult,
  defaultEnabled: boolean = true
): boolean {
  // If explicitly disabled, not enabled
  if (result.disabled_ingredients[rowId]?.has(ingredientId)) return false

  // If explicitly enabled, enabled
  if (result.enabled_ingredients[rowId]?.has(ingredientId)) return true

  // Otherwise use default
  return defaultEnabled
}

/**
 * Get the active variation for a row
 *
 * @param rowId - Row ID
 * @param result - Evaluation result
 * @returns Variation ID if set, undefined otherwise
 */
export function getActiveVariation(
  rowId: string,
  result: RuleEvaluationResult
): string | undefined {
  return result.active_variations[rowId]
}

/**
 * Get the nutrition modifier for a row
 *
 * @param rowId - Row ID
 * @param result - Evaluation result
 * @returns Nutrition modifier if set, undefined otherwise
 */
export function getNutritionModifier(
  rowId: string,
  result: RuleEvaluationResult
): NutritionModifier | undefined {
  return result.nutrition_modifiers[rowId]
}

/**
 * Get the selection limit for a row
 *
 * @param rowId - Row ID
 * @param result - Evaluation result
 * @param defaultLimit - Default limit if no rules apply
 * @returns Selection limit
 */
export function getSelectionLimit(
  rowId: string,
  result: RuleEvaluationResult,
  defaultLimit: number
): number {
  return result.selection_limits[rowId] ?? defaultLimit
}

/**
 * Create an empty rule evaluation result
 * Useful for initial state
 */
export function createEmptyRuleResult(): RuleEvaluationResult {
  return {
    visible_rows: new Set(),
    hidden_rows: new Set(),
    active_variations: {},
    enabled_ingredients: {},
    disabled_ingredients: {},
    nutrition_modifiers: {},
    selection_limits: {},
    rows_to_clear: new Map()
  }
}
